<script>
    import Vue from 'vue';
    import vPagination from '@/components/v-pagination';
    import vNoContent from '@/components/v-no-content';
    import {vIcon, vIconButton, vCheckbox} from '@/components/form';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import auth from '@/common/auth/auth';
    import renderBodyCell from './render-body-cell';
    import renderHeaderCell from './render-header-cell';
    import SortingOrders from './sorting-orders';
    import ExposeSettingsMixin from './settings/expose-settings-mixin';
    import ResizeManager from './resize-manager';
    import QueryBuilder from '@/components/query2/query-builder';

    const GridFilter = () => import('@/components/query2/wrappers/grid-filter');
    const SaveTabModal = () => import('./tabs/save-tab-modal');
    const ShareSettingsModal = () => import('./settings/share-settings-modal');
    const MergeSettingsModal = () => import('./settings/merge-settings-modal');
    const ColumnSelectionModal = () => import('./columns/column-selection-modal');

    export default {
        name: 'grid',
        mixins: [ExposeSettingsMixin],
        props: {
            /**
             * Params are used to modify the provider request externally.
             */
            params: {
                type: Object,
                default: () => ({
                    limit: 20,
                    offset: 0,
                    criteria: null,
                }),
            },
            columns: {
                type: Object,
                required: true,
            },
            options: {
                type: Object,
                default: () => ({
                    defaultSort: null,
                    defaultFilter: null,
                }),
            },
            debug: {
                type: Boolean,
                default: false,
            },
            provider: {
                type: Function,
                required: true,
            },
            tabs: {
                type: Object,
                default: null,
            },
            filterSet: {
                type: Object,
                default: null,
            },
            showColumnOptions: {
                type: Boolean,
                default: true,
            },
            queryComponent: {
                type: [Function, Object],
                default: GridFilter,
            },
            query: {
                type: [Array, Object],
                default: () => [],
            },
        },
        computed: {
            hasCheckboxes() {
                return this.currentColumns[0].type === 'checkbox';
            },
            selectedAndVisibleRows() {
                return this.rows.filter(row => this.selectedRows[row.fields.id]);
            },
            hasAllRowsSelected() {
                return this.rows.length > 0
                    && this.rows.every(row => this.selectedRows[row.fields.id]);
            },
            hasSomeRowsSelected() {
                return this.rows.length > 0
                    && this.rows.some(row => this.selectedRows[row.fields.id]);
            },
            tableClasses() {
                // todo inject different
                // classes in the future based on needs
                return 'table table-hoverable';
            },
            /**
             * Returns a list of cached columns that gets re-evaluated
             * whenever the `selectedColumns` property is modified in the column set.
             * This decouples column handling and allows external manipulation.
             */
            currentColumns() {
                return this.columns.getColumnsToRender().filter(column => column.predicate !== false);
            },
            requiresPagination() {
                return (this.rows.length && this.totalRecords > this.params.limit) || (this.params.offset > this.totalRecords);
            },
            hasNoRecords() {
                return this.totalRecords === 0 && !this.hasActiveSearchParams;
            },
            hasNoSearchResults() {
                return this.totalRecords === 0 && this.hasActiveSearchParams;
            },
            hasTabs() {
                return this.tabs !== null;
            },
            hasFilters() {
                return this.filterSet !== null;
            },
            hasTabsOrFilters() {
                return this.tabs !== null || this.filterSet !== null;
            },
            hasTabsAndFilters() {
                return this.tabs !== null && this.filterSet !== null;
            },
            hasPaginationOverflow() {
                return this.totalRecords > 0 && this.params.offset > this.totalRecords;
            },
            /**
             * When configured, returns the list of tabs currently available
             * for the grid instance.
             */
            currentTabs() {
                if (this.hasTabs) {
                    return this.tabs.getTabs();
                }
                return [];
            },
            /**
             * Determines whether the grid has search filters defined
             * either by `params` property of through an active tab.
             * Each time a search is applied `cachedProviderParams` is modified
             * to simplify tracking values.
             */
            hasActiveSearchParams() {
                if (this.cachedProviderParams !== null
                    && ((this.cachedProviderParams.filter
                        || this.cachedProviderParams.q
                        || this.cachedProviderParams.criteria
                        || null) !== null)
                ) {
                    return true;
                }
                return false;
            },
            expandParams() {
                const expand = Object.values(this.columns.getSelectableColumns())
                    .filter(column => column.expand)
                    .map(column => column.expand);
                return [...new Set(expand)].join();
            },
            canAccessCustomFields() {
                return this.columns.customFields
                    && auth.customFields.read.isAllowed;
            },
            activeTab() {
                return this.hasTabs ? this.tabs.getActiveTab() : null;
            },
            sortBy() {
                if (this.activeTab) {
                    return this.activeTab.sortBy;
                }
                return this.sortState;
            },
        },
        data() {
            return {
                eventBus: new Vue(),
                rows: [],
                totalRecords: 0,
                isLoading: true,
                isFiltersReady: false,
                serverError: false,
                activeProviderRequest: null, // active fetchData request
                cachedProviderParams: null,
                openSaveFilterModal: false,
                openShareSettingsModal: false,
                openMergeSettingsModal: false,
                openColumnSelectionModal: false,
                sortState: null,
                debounceTab: null,
                resizeManager: null,
                standaloneFilter: null,
                selectedRows: {},
                noResultImageUrl: '/static/icons/blank-state/no-result-blank-state-icon.svg',
                alertImageUrl: '/static/icons/blank-state/alert-blank-state-icon.svg',
            };
        },
        watch: {
            currentTabs() {
                this.$nextTick(() => this.tabScroll());
            },
            /* eslint-disable-next-line object-shorthand */
            '$route.fullPath'() {
                if (this.hasTabs) {
                    this.setDefaultTabSelection();
                }
            },
        },
        /**
         * Main rendering logic for the grid component.
         * @param {Function} createElement Vue rendering function
         * @returns {String}
         * @see https://vuejs.org/v2/guide/render-function.html
         */
        render(createElement) {
            let mainComponentNodes;
            if (this.serverError) {
                mainComponentNodes = [this.renderServerError(createElement)];
            } else if (this.totalRecords > 0) {
                if (this.hasPaginationOverflow) {
                    // if no records because of incorrect offset
                    mainComponentNodes = [
                        this.renderPaginationOverflow(createElement),
                        this.renderPagination(createElement),
                    ];
                } else {
                    // if there are records render the table
                    // and the pagination
                    mainComponentNodes = [
                        this.renderTable(createElement),
                        this.renderPagination(createElement),
                    ];
                }
            } else if (this.hasNoSearchResults) {
                // if we find no results based on the criteria filters
                // show a generic message
                mainComponentNodes = [this.renderNoSearchResults(createElement)];
            } else if (this.hasNoRecords && this.$slots['no-content']) {
                // if we are not searching and we have a `no-content` slot present,
                // use that as the message instead
                mainComponentNodes = [this.$slots['no-content']];
            } else {
                // otherwise render a fallback message for when there are no records
                mainComponentNodes = [this.renderFallbackNoContent(createElement)];
            }
            if (this.isLoading) {
                mainComponentNodes.unshift(this.renderLoadingState(createElement));
            }
            let componentStack = [
                this.renderGrid(createElement, mainComponentNodes),
            ];
            // when actions (action slot, column selected, tabs or filters)
            // are defined render additional components above the grid
            const tableActions = this.renderActions(createElement);
            if (tableActions) {
                componentStack = [
                    ...tableActions,
                    ...componentStack,
                ];
            }
            if (this.openSaveFilterModal) {
                componentStack = [
                    this.renderFilterModal(createElement),
                    ...componentStack,
                ];
            }
            if (this.openShareSettingsModal) {
                componentStack = [
                    this.renderShareSettingsModal(createElement),
                    ...componentStack,
                ];
            }
            if (this.openMergeSettingsModal) {
                componentStack = [
                    this.renderMergeModal(createElement),
                    ...componentStack,
                ];
            }
            if (this.openColumnSelectionModal) {
                componentStack = [
                    this.renderColumnModal(createElement),
                    ...componentStack,
                ];
            }
            if (this.$slots.title) {
                componentStack = [
                    this.renderTitle(createElement),
                    ...componentStack,
                ];
            }
            return createElement('div', {class: 'tile', ref: 'gridTile'}, componentStack);
        },
        methods: {
            renderGrid(createElement, children) {
                return createElement('div', {
                    class: 'tile-content no-padding-left no-padding-right',
                    style: {position: 'relative'},
                }, children);
            },
            renderTable(createElement) {
                return createElement('div', {
                    class: {
                        'table-control': true,
                    },
                    ref: 'gridControl',
                }, [
                    createElement(
                        'table',
                        {class: this.tableClasses, ref: 'gridTable'},
                        [
                            this.renderHeader(createElement),
                            this.renderBody(createElement),
                        ],
                    ),
                ]);
            },
            renderHeader(createElement) {
                return createElement('thead', [
                    createElement('tr', this.renderHeaderCells(createElement)),
                ]);
            },
            renderHeaderCells(createElement) {
                const renderCell = cachedColumn => renderHeaderCell({
                    cachedColumn,
                    createElement,
                    grid: this,
                });
                return this.currentColumns.map(cachedColumn => renderCell(cachedColumn));
            },
            renderBody(createElement) {
                return createElement('tbody', this.renderRows(createElement));
            },
            renderRows(createElement) {
                return this.rows.map(row => createElement('tr', this.renderSingleRow(createElement, row)));
            },
            renderSingleRow(createElement, row) {
                const renderCell = (cachedColumn) => {
                    const columnShouldBeRendered = cachedColumn.shouldRender({rowData: row});
                    return columnShouldBeRendered
                        ? renderBodyCell({
                            cachedColumn,
                            rowData: row,
                            createElement,
                            grid: this,
                        })
                        : null;
                };
                return this.currentColumns.map(cachedColumn => createElement('td', cachedColumn.getColumnProps(), [renderCell(cachedColumn)]));
            },
            renderPagination(createElement) {
                return createElement(vPagination, {
                    props: {
                        limit: this.params.limit,
                        offset: this.params.offset,
                        total: this.totalRecords,
                        showPagination: this.requiresPagination,
                    },
                    // events emitted by the pagination component
                    on: {
                        'pagination-previous': this.movePageBackward,
                        'pagination-next': this.movePageForward,
                    },
                });
            },
            renderNoSearchResults(createElement) {
                return createElement(vNoContent, {props: {imgSrc: this.noResultImageUrl}}, [
                    createElement('span', {slot: 'title'}, 'No results match your search criteria'),
                    createElement('span', {slot: 'description'}, 'Please refine your filters and try again.'),
                ]);
            },
            renderFallbackNoContent(createElement) {
                return createElement(vNoContent, {props: {imgSrc: this.noResultImageUrl}}, [
                    createElement('span', {slot: 'title'}, 'No records found.'),
                    createElement('span', {slot: 'description'}, 'There are no records available for this page.'),
                ]);
            },
            renderPaginationOverflow(createElement) {
                return createElement(vNoContent, {props: {imgSrc: this.alertImageUrl}}, [
                    createElement('span', {slot: 'title'}, 'Cannot display your saved configuration.'),
                    createElement('span', {slot: 'description'}, 'Try to open previous page.'),
                ]);
            },
            renderServerError(createElement) {
                return createElement(vNoContent, {props: {imgSrc: this.alertImageUrl}}, [
                    createElement('span', {slot: 'title'}, 'There was an error...'),
                    createElement('span', {slot: 'description'}, 'Rebilly was not able to process your request. Please try again later.'),
                ]);
            },
            renderLoadingState(createElement) {
                return createElement('div', {class: 'loader loader-grid is-loading'}, [
                    this.renderLoadingBox(createElement),
                ]);
            },
            renderLoadingBox(createElement) {
                return createElement('div', {class: 'loading is-loading'}, [
                    createElement('div', {class: 'mr-re'}, ''),
                    createElement('div', {class: 'mr-bil'}, ''),
                    createElement('div', {class: 'mr-ly'}, ''),
                ]);
            },
            renderActions(createElement) {
                const elements = [];
                const tabAndActionGroup = this.renderTabAndActionsGroup(createElement);
                if (tabAndActionGroup) {
                    elements.push(tabAndActionGroup);
                }
                if (this.hasFilters) {
                    elements.push(this.renderFilters(createElement));
                }
                if (this.hasCheckboxes) {
                    elements.push(this.renderBatchActions(createElement));
                }
                return elements.length && elements;
            },
            renderTabAndActionsGroup(createElement) {
                const elements = [];
                if (this.hasTabs) {
                    elements.push(createElement('ul', {class: 'tab', ref: 'gridTabs'}, this.renderTabs(createElement)));
                } else if (!this.hasTabs && this.$slots['left-action']) {
                    // if we don't have tabs but we have a
                    // slot for the left side render it instead
                    elements.push(this.$slots['left-action']);
                }
                const actionsGroup = this.renderActionGroup(createElement);
                if (actionsGroup) {
                    elements.push(actionsGroup);
                }
                return elements.length && createElement('div', {class: 'table-actions'}, elements);
            },
            renderTabs(createElement) {
                const activeTabUid = this.activeTab ? this.activeTab.uid : null;
                return this.currentTabs.map(tab => createElement(
                    'li',
                    {class: 'tab-item'},
                    [
                        createElement(
                            'a',
                            {
                                class: `tab-link${tab.uid === activeTabUid ? ' is-active' : ''}`,
                                on: {
                                    click: () => this.setActiveTab(tab),
                                },
                            },
                            tab.label,
                        ),
                    ],
                ));
            },
            renderBatchActions(createElement) {
                const count = this.selectedAndVisibleRows.length;
                const batchActionButtons = this.columns.batchActions.map(({
                    icon, label, handler, predicate, component = null,
                }) => {
                    if (component && !!predicate) {
                        return createElement(component, {
                            props: {
                                predicate,
                                grid: this,
                                rows: this.selectedAndVisibleRows,
                            },
                        });
                    }
                    return createElement(
                        vIconButton,
                        {
                            class: 'inline-s has-icon-left',
                            props: {
                                type: 'basic',
                                disabled: (count < 1) || (predicate === false),
                            },
                            on: {
                                click: () => handler(this, this.selectedAndVisibleRows),
                            },
                        },
                        [
                            createElement(vIcon, {props: {icon}}),
                            label,
                        ],
                    );
                });
                const batchCheckbox = createElement(
                    vCheckbox,
                    {
                        props: {
                            selected: this.hasAllRowsSelected,
                            fuzzy: !this.hasAllRowsSelected && this.hasSomeRowsSelected,
                            value: true,
                            label: null,
                        },
                        on: {
                            input: (value) => {
                                if (value === true) {
                                    this.selectVisibleRows();
                                } else {
                                    this.unselectVisibleRows();
                                }
                            },
                        },
                    },
                );
                const selectedRowCount = createElement('span',
                                                       {class: 'text-muted inline-m'},
                                                       `${count} ${count === 1 ? 'row' : 'rows'} selected`);
                return createElement(
                    'div',
                    {class: 'batch-actions'},
                    [
                        batchCheckbox,
                        selectedRowCount,
                        createElement(
                            'div',
                            {class: 'batch-action-buttons'},
                            batchActionButtons,
                        ),

                    ],
                );
            },
            renderFilters(createElement) {
                const {hasTabs} = this;
                let filterSlot = [];
                if (hasTabs && !this.activeTab.systemTab) {
                    filterSlot = [
                        createElement(
                            'div',
                            {slot: 'left-actions'},
                            [
                                ...this.activeTab.readOnly
                                    ? [
                                        this.renderShareFilterButton(createElement),
                                        this.renderEditFilterButton(createElement),
                                        this.renderDeleteFilterButton(createElement),
                                    ]
                                    : [this.renderSaveFilterButton(createElement)],
                            ],
                        ),
                    ];
                }
                const filterComponent = createElement(
                    this.queryComponent,
                    {
                        ref: 'query',
                        props: {
                            query: hasTabs ? this.activeTab.filter : this.query,
                            filterSet: this.filterSet,
                        },
                        on: {
                            ready: this.handleFiltersIsReady,
                            update: this.handleFiltersUpdated,
                        },
                    },
                    // slot in previous buttons
                    filterSlot,
                );
                return createElement(
                    'div',
                    {class: 'tile-content tile-content-secondary tile-content-filter padding-top-m'},
                    [
                        createElement(
                            'div',
                            {class: 'grid-content'},
                            [
                                createElement(
                                    'div',
                                    {class: 'grid-item grid-item-compact'},
                                    [filterComponent],
                                ),
                            ],
                        ),
                    ],
                );
            },
            renderSaveFilterButton(createElement) {
                return createElement(
                    'button',
                    {
                        class: 'btn',
                        on: {
                            click: () => {
                                this.openSaveFilterModal = true;
                            },
                        },
                    },
                    'Save Filter',
                );
            },
            renderEditFilterButton(createElement) {
                return createElement(
                    vIconButton,
                    {
                        class: {
                            'inline-s': true,
                        },
                        on: {
                            click: () => {
                                this.openSaveFilterModal = true;
                            },
                        },
                        props: {
                            tooltip: 'Edit filter name',
                        },
                    },
                    [
                        createElement(
                            vIcon,
                            {
                                props: {
                                    icon: 'edit',
                                },
                            },
                        ),
                    ],
                );
            },
            renderShareFilterButton(createElement) {
                return createElement(
                    vIconButton,
                    {
                        class: {
                            'inline-s': true,
                        },
                        on: {
                            click: () => {
                                this.openShareSettingsModal = true;
                            },
                        },
                        props: {
                            tooltip: 'Share current tab',
                        },
                    },
                    [
                        createElement(
                            vIcon,
                            {
                                props: {
                                    icon: 'data-exports',
                                },
                            },
                        ),
                    ],
                );
            },
            renderDeleteFilterButton(createElement) {
                return createElement(
                    vIconButton,
                    {
                        on: {
                            click: this.showDeleteFilterDialog,
                        },
                        props: {
                            tooltip: 'Delete filter',
                            tooltipRightEdge: true,
                        },
                    },
                    [
                        createElement(
                            vIcon,
                            {
                                props: {
                                    icon: 'trash',
                                },
                            },
                        ),
                    ],
                );
            },
            renderFilterModal(createElement) {
                return createElement(
                    SaveTabModal,
                    {
                        props: {
                            name: this.activeTab.systemTab ? undefined : this.activeTab.label,
                        },
                        on: {
                            close: () => {
                                this.openSaveFilterModal = false;
                            },
                            save: this.saveNewTabToPreferences,
                        },
                    },
                );
            },
            renderShareSettingsModal(createElement) {
                return createElement(
                    ShareSettingsModal,
                    {
                        props: {
                            url: this.generateSettingsUrl(),
                        },
                        on: {
                            close: () => {
                                this.openShareSettingsModal = false;
                            },
                        },
                    },
                );
            },
            renderMergeModal(createElement) {
                return createElement(
                    MergeSettingsModal,
                    {
                        props: {
                            settings: this.getExposedSettings(),
                        },
                        on: {
                            close: () => {
                                this.openMergeSettingsModal = false;
                                this.removeGridSettingsFromRoute();
                                this.setExposedSettings(null);
                            },
                            override: () => {
                                this.openMergeSettingsModal = false;
                                this.mergeTabsSettings();
                            },
                        },
                    },
                );
            },
            renderColumnModal(createElement) {
                return createElement(
                    ColumnSelectionModal,
                    {
                        props: {
                            columnSet: this.columns,
                        },
                        on: {
                            close: () => {
                                this.openColumnSelectionModal = false;
                            },
                        },
                    },
                );
            },
            renderActionGroup(createElement) {
                const actions = [];
                if (this.$slots.actions) {
                    actions.push(this.$slots.actions);
                }
                if (this.showColumnOptions) {
                    actions.push(this.renderRefreshButton(createElement));
                    actions.push(this.renderColumnWidthResetButton(createElement));
                    actions.push(this.renderColumnSelectionButton(createElement));
                }
                return actions.length
                    && createElement('div', {class: 'table-action-group', ref: 'gridTabActions'}, [actions]);
            },
            renderColumnSelectionButton(createElement) {
                return createElement(
                    vIconButton,
                    {
                        class: 'btn-basic inline-s',
                        on: {
                            click: () => {
                                this.openColumnSelectionModal = true;
                            },
                        },
                        props: {
                            tooltip: 'Edit/Reorder Columns',
                            tooltipRightEdge: true,
                        },
                    },
                    [
                        createElement(
                            vIcon,
                            {
                                props: {icon: 'columns'},
                            },
                        ),
                    ],
                );
            },
            renderColumnWidthResetButton(createElement) {
                return createElement(
                    vIconButton,
                    {
                        class: 'btn-basic inline-xs',
                        on: {
                            click: () => {
                                if (this.resizeManager) {
                                    this.resizeManager.resetWidths();
                                }
                            },
                        },
                        props: {
                            tooltip: 'Reset column widths',
                        },
                    },
                    [
                        createElement(
                            vIcon,
                            {
                                props: {icon: 'resize-columns'},
                            },
                        ),
                    ],
                );
            },
            renderRefreshButton(createElement) {
                return createElement(
                    vIconButton,
                    {
                        class: 'btn-basic inline-xs',
                        on: {
                            click: () => {
                                this.fetchData();
                            },
                        },
                        props: {
                            tooltip: 'Refresh Grid',
                        },
                    },
                    [
                        createElement(
                            vIcon,
                            {
                                props: {icon: 'reset'},
                            },
                        ),
                    ],
                );
            },
            renderTitle(createElement) {
                return createElement(
                    'div',
                    {class: 'tile-title'},
                    [this.$slots.title],
                );
            },
            /**
             * Save a tab to the profile with the `filterName` as the `label`
             * of the tab. Once saved the tab will be restored each time
             * the page with the current grid instance is loaded.
             */
            saveNewTabToPreferences(filterName) {
                this.openSaveFilterModal = false;
                this.activeTab.readOnly = true;
                this.activeTab.label = filterName;
                this.saveTabsToPreferences();
            },
            /**
             * Delegate the async call to save tabs within the user's
             * preferences via the API.
             */
            saveTabsToPreferences() {
                (async () => {
                    if (this.tabs) { await this.tabs.saveTabsToPreferences(); }
                })();
            },
            saveSortToPreferences() {
                if (!this.hasTabs && this.hasFilters) {
                    (async () => {
                        await this.filterSet.saveSortToPreferences(this.sortState);
                    })();
                }
            },
            /**
             * Apply tab settings
             */
            mergeTabsSettings() {
                const exposedSettings = this.getExposedSettings();
                const exposedTab = exposedSettings && exposedSettings.tab;
                const exposedColumns = exposedSettings && exposedSettings.selectedColumns;
                this.setExposedSettings(null);
                if (exposedTab) {
                    const allTabs = this.tabs.getTabs();
                    this.tabs.deleteCustomTab(allTabs.find(tab => tab.label === exposedTab.label));
                    this.setActiveTab(this.tabs.addCustomTab({
                        ...exposedTab,
                        readOnly: true,
                    }));
                }
                if (exposedColumns) {
                    this.columns.setSelectedColumns(exposedColumns);
                }
            },
            /**
             * Set the default tab selection if there is no active tab.
             * Picks the first tab within the configuration found in `tabs`.
             */
            setDefaultTabSelection() {
                const exposedSettings = this.getExposedSettings();
                if (exposedSettings && exposedSettings.tab) {
                    const tabs = this.tabs.getTabs();
                    const exposedTab = exposedSettings.tab;
                    const currentTab = tabs.find(tab => tab.label === exposedTab.label);
                    if (currentTab) {
                        // only ask the user to merge tabs if the filters actually change
                        if (!this.exposedWillMutateTab(exposedTab, currentTab)) {
                            this.openMergeSettingsModal = false;
                            this.tabs.setActiveTab(currentTab.uid);
                            this.setExposedSettings(null);
                        } else {
                            this.openMergeSettingsModal = true;
                        }
                    } else {
                        // if no match, create new custom tab
                        this.setActiveTab(this.tabs.addCustomTab({
                            ...exposedSettings.tab,
                            readOnly: !exposedTab.unsaved,
                        }));
                        this.setExposedSettings(null);
                    }
                    if (exposedSettings.selectedColumns) {
                        this.columns.setSelectedColumns(exposedSettings.selectedColumns);
                    }
                }
                if (!this.activeTab && this.currentTabs.length) {
                    const [firstTab] = this.currentTabs;
                    this.tabs.setActiveTab(firstTab.uid);
                    this.saveTabsToPreferences();
                }
                if (this.activeTab && this.activeTab.systemTab && !this.activeTab.sortBy) {
                    this.activeTab.sortBy = this.getInitialSort();
                }
            },
            /**
             * Set the active tab when the user clicks on one in the interface.
             * @param {FilterTab} tab
             */
            setActiveTab(tab) {
                if (this.activeTab && this.activeTab.uid === tab.uid) {
                    return;
                }
                if (this.activeTab && !this.activeTab.readOnly) {
                    // custom search doesn't saved
                    this.tabs.deleteCustomTab(this.activeTab);
                }
                this.tabs.setActiveTab(tab.uid);
                this.saveTabsToPreferences();
                this.fetchData({resetOffset: true});
            },
            /**
             * Decide whether to update the current active tab condition
             * or create a new tab.
             */
            updateActiveTabFilters(data) {
                if (this.activeTab.readOnly && this.activeTab.systemTab) {
                    this.setActiveTab(this.tabs.addCustomTab({
                        filter: data,
                        sortBy: this.sortBy,
                        readOnly: false,
                    }));
                } else if (data.length) {
                    // TODO check tab behavior with new filters
                    // the filters was added on custom not-saved tab
                    this.updateTabAndFetch(data);
                } else {
                    // the filters was deleted on custom not-saved tab
                    this.deleteActiveTab();
                }
            },
            updateStandaloneFilters(data) {
                // the filters was added on custom not-saved tab
                this.standaloneFilter = data;
                this.fetchData({resetOffset: true});
            },
            handleFiltersIsReady() {
                this.isFiltersReady = true;
            },
            handleFiltersUpdated(data) {
                if (this.hasTabs) {
                    this.updateActiveTabFilters(data);
                } else {
                    this.updateStandaloneFilters(data);
                }
                this.unselectAllRows();
            },
            /**
             * Update the current active tab search and
             * fetch a new set of data from the API with the
             * new criteria.
             */
            updateTabAndFetch(data) {
                this.activeTab.setFilter(data);
                this.saveTabsToPreferences();
                this.fetchData({resetOffset: true});
            },
            /**
             * Delete the current active tab, select the first tab
             * in the list and fetch its matching data set.
             */
            async showDeleteFilterDialog() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Delete Filter',
                    content: 'Are you sure you want to delete this filter?',
                    confirmLabel: 'Delete',
                    dangerConfirm: true,
                };
                if (await confirmDialog(dialogParams)) {
                    this.deleteActiveTab();
                }
            },
            deleteActiveTab() {
                this.tabs.deleteCustomTab(this.activeTab);
                this.tabs.setActiveTab(null);
                this.setDefaultTabSelection();
                this.saveTabsToPreferences();
                this.fetchData({resetOffset: true});
            },
            /**
             * @param {boolean} resetOffset Define whether the offset (pagination) must be reset.
             * This usually happens when switching filters (tabs).
             */
            fetchData({resetOffset = false} = {}) {
                this.$emit('fetching');
                this.startLoading();
                let isCanceled = false;
                (async () => {
                    try {
                        // cancel previous request
                        if (this.isLoading && this.activeProviderRequest && this.activeProviderRequest.cancel) {
                            this.activeProviderRequest.cancel();
                        }
                        this.activeProviderRequest = this.provider(this.getProviderParams({resetOffset}));
                        const [result] = await Promise.all([
                            this.activeProviderRequest,
                            // when required also fetch custom fields for use in the grid
                            // columns
                            this.canAccessCustomFields
                                ? this.$store.dispatch('rulesEngine/fetchCustomFields', this.columns.customFields)
                                : () => {
                                },
                        ]);
                        this.serverError = false;
                        this.activeProviderRequest = null;
                        this.updatePagination(result);
                        this.rows = result.items;
                    } catch (error) {
                        if (error && error.name === 'RebillyCanceledError') {
                            // ignore RebillyCanceledError (cancel request)
                            isCanceled = true;
                        } else {
                            this.serverError = true;
                        }
                    } finally {
                        if (!isCanceled) {
                            this.stopLoading();
                            if (this.isFiltersReady) {
                                this.$emit('completed');
                            } else if (this.$refs.query && this.filterSet !== null) {
                                // wait while query filter will be ready
                                this.$refs.query.$once('ready', () => {
                                    this.$emit('completed');
                                });
                            } else {
                                this.$emit('completed');
                            }
                        }
                        this.$nextTick(() => {
                            this.tabScroll();
                            this.checkGridSize();
                        });
                    }
                })();
            },
            startLoading() {
                this.isLoading = true;
            },
            stopLoading() {
                this.isLoading = false;
            },
            /**
             * Compute the parameters to use for the next API call.
             * @param {boolean} resetOffset Define whether the offset (pagination) must be reset.
             * This usually happens when switching filters (tabs).
             */
            getProviderParams({resetOffset}) {
                let params = {};
                if (this.params.limit) {
                    params.limit = this.params.limit;
                }
                if (resetOffset) {
                    // reset offset to zero before adding it to the payload
                    this.params.offset = 0;
                }
                if (this.params.offset) {
                    params.offset = this.params.offset;
                }
                if (this.sortBy) {
                    params.sort = this.getSortFromConfig(this.sortBy);
                }
                if (this.hasTabs) {
                    params = {
                        ...params,
                        ...this.getTabProviderParams(),
                    };
                } else if (this.hasFilters) {
                    params = {
                        ...params,
                        ...this.getFilterProviderParams(),
                    };
                }
                if (Object.keys(params).length) {
                    this.cachedProviderParams = this.$clonePlainObject(params);
                }
                if (this.expandParams) {
                    params = {
                        ...params,
                        expand: this.expandParams,
                    };
                }
                return params;
            },
            /**
             * Given a configuration, extract the sorting value
             * for API requests.
             */
            getSortFromConfig(config) {
                const [field] = Object.keys(config);
                return `${config[field] === SortingOrders.ascending ? '' : '-'}${field}`;
            },
            /**
             * Return criteria to apply to the API request if
             * the active tab has conditions.
             */
            getTabProviderParams() {
                if (this.activeTab) {
                    const params = {};
                    if (this.activeTab.filter) {
                        const filterSet = this.hasTabsAndFilters
                            ? this.filterSet : null;
                        params.filter = this.activeTab.getStringFilter(filterSet);
                        return params;
                    }
                    if (this.activeTab.offset) {
                        params.offset = this.activeTab.offset;
                        this.params.offset = params.offset;
                    }
                    // If the grid doesn't have a sorting value use the tab's
                    // config if present. This will make the tab sorting take over
                    // whenever the user resets sorting on a column, thus resetting
                    // the grid to a `default` state
                    if (this.sortBy === null && this.activeTab.sortBy) {
                        params.sort = this.getSortFromConfig(this.activeTab.sortBy);
                    }
                    return params;
                }
                return {};
            },
            getFilterProviderParams() {
                // `standaloneFilter` is a query array
                if (this.standaloneFilter) {
                    return {
                        filter: QueryBuilder.convertQueryToFilterString({
                            filterSet: this.filterSet,
                            query: this.standaloneFilter,
                        }),
                    };
                }
                return {};
            },
            updatePagination({total}) {
                this.totalRecords = Number(total);
            },
            movePageBackward() {
                if (this.params.offset >= this.params.limit) {
                    this.params.offset = this.params.offset - this.params.limit;
                    if (this.activeTab) {
                        this.activeTab.offset = this.params.offset;
                    }
                    this.fetchData();
                    this.saveTabsToPreferences();
                }
            },
            movePageForward() {
                if (this.params.offset < this.totalRecords) {
                    this.params.offset = this.params.offset + this.params.limit;
                    if (this.activeTab) {
                        this.activeTab.offset = this.params.offset;
                    }
                    this.fetchData();
                    this.saveTabsToPreferences();
                }
            },
            /**
             * Get initial tab sort.
             */
            getInitialSort() {
                if (this.options.defaultSort !== null) {
                    return {...this.options.defaultSort};
                }
                return null;
            },
            /**
             * Change the current grid sort based on the `field` value received.
             * If the grid is already sorting on the desired column,
             * we toggle through [ascending -> descending -> no sort],
             * otherwise we start an ascending sort on the new column field.
             */
            sortChange({field}) {
                // default sort to descending
                let sortChange = {[field]: SortingOrders.descending};
                if (this.sortBy && this.sortBy[field]) {
                    if (this.sortBy[field] === SortingOrders.descending) {
                        // already sorting asc, thus going desc
                        sortChange = {[field]: SortingOrders.ascending};
                    } else {
                        // sorting again from ascending, thus cancelling sort on column
                        sortChange = null;
                    }
                }
                // sort was removed
                if (sortChange === null) {
                    // apply a default sort
                    if (this.activeTab && this.activeTab.defaultSortBy) {
                        sortChange = this.activeTab.defaultSortBy;
                    } else if (this.hasFilters) {
                        sortChange = this.filterSet.defaultSortBy;
                    }
                }
                this.setSortState(sortChange);
                // TODO remove once segments are merged
                this.saveTabsToPreferences();
                this.saveSortToPreferences();
                this.fetchData();
            },
            /**
             * Save the current state of the grid sort depending
             * on whether there are tabs or not.
             * @param {Object} sortChange
             */
            setSortState(sortChange) {
                this.$emit('sort-change', sortChange);
                if (this.activeTab) {
                    this.activeTab.setSort(sortChange);
                } else {
                    this.sortState = sortChange;
                }
            },
            /**
             * Prepares the first data fetch of the grid. If tabs/filters are present
             * then we also set the default active tab, and the initial condition set.
             */
            prepareFirstFetch() {
                if (this.hasFilters && !this.hasTabs) {
                    // set initial filter
                    this.standaloneFilter = this.filterSet.query;
                    this.setSortState(this.filterSet.sortBy);
                    // note that once filters load external data they will trigger
                    // a data fetch with the correct query
                } else {
                    if (this.hasTabs) {
                        this.setDefaultTabSelection();
                    }
                    this.fetchData();
                }
            },
            tabScroll() {
                const {gridTabs} = this.$refs;
                const activeTab = gridTabs ? gridTabs.querySelector('.is-active') : null;

                if (activeTab) {
                    gridTabs.scrollTo(activeTab.offsetLeft - 16, 0);
                }
            },
            scrollDebounce(func) {
                let timeout;
                const delegate = () => {
                    if (timeout) {
                        window.cancelAnimationFrame(timeout);
                    }
                    timeout = window.requestAnimationFrame(func);
                };
                window.addEventListener('resize', delegate);
                return delegate;
            },
            checkGridSize() {
                this.debounceTab = this.scrollDebounce(this.tabScroll);
            },
            onColumnResize(widths) {
                this.columns.setColumnWidths(widths);
            },
            selectVisibleRows() {
                this.rows.forEach((row) => {
                    this.$set(this.selectedRows, row.fields.id, true);
                });
            },
            unselectVisibleRows() {
                this.rows.forEach((row) => {
                    this.$set(this.selectedRows, row.fields.id, false);
                });
            },
            unselectAllRows() {
                this.selectedRows = {};
            },
        },
        created() {
            /* eslint-disable no-console */
            if (this.debug) {
                console.time('Time to compose grid');
                console.time('Time to mount grid');
            }
            this.eventBus.$on('sort-change', this.sortChange);
            this.prepareFirstFetch();
        },
        beforeMount() {
            /* eslint-disable no-console */
            if (this.debug) {
                console.timeEnd('Time to compose grid');
            }
        },
        mounted() {
            /* eslint-disable no-console */
            if (this.debug) {
                console.timeEnd('Time to mount grid');
            }
        },
        updated() {
            if (this.showColumnOptions && this.$refs.gridTable) {
                const widths = {
                    // TODO make it more obvious that these two arrays are based on the same set of columns
                    userSetWidths: this.columns.getColumnWidths(),
                    defaultWidths: this.currentColumns.map(column => column.props.defaultWidth),
                };
                if (this.resizeManager) {
                    this.resizeManager.destroy();
                }
                this.resizeManager = new ResizeManager(this.$refs.gridTable, widths, this.onColumnResize);
            }
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.debounceTab);
            if (this.resizeManager) {
                this.resizeManager.destroy();
            }
        },
    };
</script>
